import { useMemo } from 'react';
import { Button, Input, Tooltip, Space, Dropdown, Checkbox, Badge } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  ClearOutlined,
  SearchOutlined,
  DownloadOutlined,
  AppstoreOutlined,
  UndoOutlined,
  FilterOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  GroupOutlined
} from '@ant-design/icons';

// Toolbar for the ERP grid. Two-row pattern on narrow widths, single-
// row on desktops. Left: title + record-count. Right: search +
// column-toggle + export + refresh + clear + add.
//
// Owned by ErpDataGrid; not designed to be mounted standalone — its
// props are tuned to be passed directly from the grid's internal state.

export default function ErpDataGridToolbar({
  title,
  searchable,
  searchValue,
  searchPlaceholder,
  searchInputRef,
  onSearchChange,
  kbdHint,
  canAdd,
  onAdd,
  addButtonText = 'Add',
  onRefresh,
  onClearFilters,
  // Column visibility — receives an array of leaf columns
  // ({ key, title, dataIndex, fixed }) and the current hidden Set.
  leafColumns = [],
  hiddenColumnKeys,
  onColumnVisibilityChange,
  // Export — when truthy + onExport provided, button is enabled and
  // calls back with the current filtered + sorted dataSource snapshot.
  // When `onExportSelected` is also provided AND `selectedCount` > 0,
  // the Export control becomes a Dropdown with two items:
  // "Export current view" and "Export selected (N)".
  exportable,
  onExport,
  onExportSelected,
  selectedCount = 0,
  // Reset persisted grid layout — surfaced as the last item under
  // the "Columns" dropdown when defined. Hidden otherwise so grids
  // without persistence don't show a dead control.
  onResetLayout,
  // Move Up / Move Down column reorder — exposed inline on each
  // entry in the Columns dropdown. The wiring is fully optional;
  // callers without persistence don't pass these and the arrows
  // simply don't render.
  onColumnMoveUp,
  onColumnMoveDown,
  // Advanced filter modal trigger. When `onAdvancedFilter` is set
  // a Filters button appears with a badge showing the active rule
  // count.
  onAdvancedFilter,
  advancedFilterCount = 0,
  // Grouping (Stage 6A Increment 2) — when `groupableColumns` has at
  // least one entry, a "Group by" dropdown appears. Items map to
  // `column.dataIndex` values; the current `groupByKey` shows a check
  // mark. `null` (or the "(None)" item) clears grouping.
  groupableColumns,
  groupByKey,
  onGroupByChange,
  extra,
  totalRows,
  filteredRows
}) {
  const showSearchCount =
    searchValue && filteredRows !== undefined && filteredRows !== totalRows;

  // ── Column visibility dropdown items ──
  // Each row carries:
  //   - a Checkbox for hide/show (disabled when the column is `fixed`
  //     or `locked`)
  //   - optional Up/Down arrows for reorder (rendered only when both
  //     handlers are wired; disabled at the endpoints of the list)
  //
  // Locked + fixed columns participate in the reorder buttons too,
  // but the grid is responsible for refusing to move them past their
  // valid range (e.g. a `fixed: 'left'` column shouldn't slide to the
  // right). Defensive bounds-checks live in the grid's handler.
  const canReorder =
    typeof onColumnMoveUp === 'function' &&
    typeof onColumnMoveDown === 'function';

  const columnMenuItems = useMemo(() => {
    const cols = leafColumns || [];
    const items = cols.map((col, idx) => {
      const key = col.key || col.dataIndex;
      const isHidden = hiddenColumnKeys && hiddenColumnKeys.has(key);
      const isFixed = Boolean(col.fixed);
      const isLocked = Boolean(col.locked) || isFixed;
      return {
        key,
        // Render the row as a row containing the AntD Checkbox plus the
        // reorder arrows. We mark the surrounding row `flex` so the
        // controls right-align consistently regardless of label length.
        label: (
          <div className="flex items-center justify-between gap-2 w-full">
            <Checkbox
              checked={!isHidden}
              disabled={isLocked}
              onChange={(e) => {
                if (typeof onColumnVisibilityChange === 'function') {
                  onColumnVisibilityChange(key, !e.target.checked);
                }
              }}
              className="flex-1 min-w-0"
            >
              <span className="text-[12.5px] text-ink-700">
                {typeof col.title === 'string' ? col.title : key}
              </span>
              {isLocked ? (
                <span className="ml-2 text-[10.5px] text-ink-400 uppercase tracking-wider">
                  {isFixed ? 'pinned' : 'locked'}
                </span>
              ) : null}
            </Checkbox>
            {canReorder ? (
              <Space size={2} onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Move up">
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${typeof col.title === 'string' ? col.title : key} up`}
                    icon={<ArrowUpOutlined />}
                    disabled={idx === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      onColumnMoveUp(key);
                    }}
                  />
                </Tooltip>
                <Tooltip title="Move down">
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${typeof col.title === 'string' ? col.title : key} down`}
                    icon={<ArrowDownOutlined />}
                    disabled={idx === cols.length - 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      onColumnMoveDown(key);
                    }}
                  />
                </Tooltip>
              </Space>
            ) : null}
          </div>
        )
      };
    });
    if (typeof onResetLayout === 'function') {
      items.push({ type: 'divider', key: '__divider_reset__' });
      items.push({
        key: '__reset_layout__',
        icon: <UndoOutlined />,
        label: 'Reset layout',
        onClick: onResetLayout
      });
    }
    return items;
  }, [
    leafColumns,
    hiddenColumnKeys,
    canReorder,
    onColumnVisibilityChange,
    onColumnMoveUp,
    onColumnMoveDown,
    onResetLayout
  ]);

  // ── Group-by dropdown items ──
  // First entry is always "(None)" to clear grouping. Subsequent
  // entries are derived from columns marked `groupable: true`. The
  // current selection shows a check mark in the menu icon slot.
  const groupMenuItems = useMemo(() => {
    if (!Array.isArray(groupableColumns) || groupableColumns.length === 0) {
      return [];
    }
    const items = [
      {
        key: '__none__',
        label: 'No grouping',
        onClick: () => {
          if (typeof onGroupByChange === 'function') onGroupByChange(null);
        }
      },
      { type: 'divider', key: '__divider_group__' }
    ];
    for (const col of groupableColumns) {
      const k =
        col?.dataIndex ||
        col?.key ||
        (typeof col?.title === 'string' ? col.title : null);
      if (!k) continue;
      const selected = k === groupByKey;
      items.push({
        key: String(k),
        label: typeof col.title === 'string' ? col.title : String(k),
        // Use a leading bullet + space when selected so the label
        // doesn't shift when toggled — keeps menu visually stable.
        icon: selected ? <span aria-hidden="true">●</span> : null,
        onClick: () => {
          if (typeof onGroupByChange === 'function') onGroupByChange(k);
        }
      });
    }
    return items;
  }, [groupableColumns, groupByKey, onGroupByChange]);

  const groupingActive = Boolean(groupByKey);
  const groupingLabel = useMemo(() => {
    if (!groupingActive) return null;
    const match = (groupableColumns || []).find(
      (c) => (c.dataIndex || c.key) === groupByKey
    );
    return match && typeof match.title === 'string' ? match.title : groupByKey;
  }, [groupingActive, groupableColumns, groupByKey]);

  // ── Export dropdown items (lazy — Dropdown is only used when both
  // onExport AND onExportSelected are wired). Single-handler callers
  // get the original single-button behaviour.
  const exportMenuItems = useMemo(() => {
    if (typeof onExport !== 'function' && typeof onExportSelected !== 'function') {
      return [];
    }
    return [
      {
        key: 'current',
        icon: <DownloadOutlined />,
        label: 'Export current view',
        disabled: typeof onExport !== 'function',
        onClick: onExport
      },
      {
        key: 'selected',
        icon: <DownloadOutlined />,
        label: `Export selected${selectedCount ? ` (${selectedCount})` : ''}`,
        disabled:
          typeof onExportSelected !== 'function' || selectedCount === 0,
        onClick: onExportSelected
      }
    ];
  }, [onExport, onExportSelected, selectedCount]);

  // We deliberately don't render the inline total-count here — the
  // pagination footer's `Showing X–Y of Z records` already carries
  // that information, and showing both made the toolbar feel noisy.
  // The narrower derived `showSearchCount` indicator (`12 of 25`) is
  // still surfaced because it changes live as the user types.
  return (
    <div className="erp-grid__toolbar flex flex-wrap items-center gap-3 mb-3">
      {title ? (
        <div className="erp-grid__toolbar-title-wrap min-w-0 flex items-baseline gap-2">
          {/* Stage 6A.2K — `erp-grid__title` is the token hook for the
              header-theme polish; the Tailwind classes stay for layout
              compat with the rest of the toolbar. */}
          <h2 className="erp-grid__title m-0 text-base font-semibold text-ink-900 tracking-tight truncate">
            {title}
          </h2>
          {showSearchCount ? (
            <span className="erp-grid__search-count text-xs text-ink-500 tabular-nums">
              <span className="font-semibold text-ink-700">
                {filteredRows.toLocaleString()}
              </span>{' '}
              of {totalRows.toLocaleString()}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {searchable ? (
          <Input
            ref={searchInputRef}
            allowClear
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder || 'Search…'}
            prefix={<SearchOutlined className="erp-grid__search-icon" />}
            suffix={
              kbdHint ? (
                <kbd className="erp-grid__kbd" aria-hidden="true">
                  {kbdHint}
                </kbd>
              ) : null
            }
            // Width + transitions are scoped in `.erp-grid__search`
            // (see index.css). Focus expands the input from 240 → 320
            // with an ease-out transition for a subtle, professional feel.
            className="erp-grid__search"
            size="middle"
          />
        ) : null}

        <Space size={6}>
          {/* Column visibility */}
          {leafColumns && leafColumns.length > 0 ? (
            <Dropdown
              menu={{ items: columnMenuItems }}
              trigger={['click']}
              placement="bottomRight"
              overlayClassName="erp-grid__col-menu"
            >
              <Tooltip title="Show / hide columns" placement="top">
                <Button
                  icon={<AppstoreOutlined />}
                  aria-label="Toggle columns"
                  className="!h-9 !rounded-lg !font-medium"
                >
                  Columns
                </Button>
              </Tooltip>
            </Dropdown>
          ) : null}

          {typeof onAdvancedFilter === 'function' ? (
            <Tooltip title="Advanced filter" placement="top">
              <Badge
                count={advancedFilterCount}
                size="small"
                offset={[-4, 4]}
                color="#2563EB"
              >
                <Button
                  icon={<FilterOutlined />}
                  onClick={onAdvancedFilter}
                  aria-label="Open advanced filter"
                  className="!h-9 !rounded-lg !font-medium"
                >
                  Filters
                </Button>
              </Badge>
            </Tooltip>
          ) : null}

          {groupMenuItems.length > 0 ? (
            <Dropdown
              menu={{ items: groupMenuItems }}
              trigger={['click']}
              placement="bottomRight"
              overlayClassName="erp-grid__group-menu"
            >
              <Tooltip
                title={
                  groupingActive
                    ? `Grouped by ${groupingLabel}`
                    : 'Group rows by column'
                }
                placement="top"
              >
                <Button
                  icon={<GroupOutlined />}
                  aria-label="Group by"
                  className={`!h-9 !rounded-lg !font-medium ${
                    groupingActive ? '!border-brand-300 !text-brand-700' : ''
                  }`}
                >
                  {groupingActive ? `Group · ${groupingLabel}` : 'Group'}
                </Button>
              </Tooltip>
            </Dropdown>
          ) : null}

          {typeof onRefresh === 'function' ? (
            <Tooltip title="Refresh" placement="top">
              <Button
                icon={<ReloadOutlined />}
                onClick={onRefresh}
                aria-label="Refresh"
                className="!h-9 !rounded-lg !font-medium"
              />
            </Tooltip>
          ) : null}

          {typeof onClearFilters === 'function' ? (
            <Tooltip title="Clear search, filters and sort" placement="top">
              <Button
                icon={<ClearOutlined />}
                onClick={onClearFilters}
                aria-label="Clear filters"
                className="!h-9 !rounded-lg !font-medium"
              >
                Clear
              </Button>
            </Tooltip>
          ) : null}

          {exportable ? (
            typeof onExportSelected === 'function' ? (
              <Dropdown
                menu={{ items: exportMenuItems }}
                trigger={['click']}
                placement="bottomRight"
                overlayClassName="erp-grid__export-menu"
              >
                <Tooltip title="Export as CSV" placement="top">
                  <Button
                    icon={<DownloadOutlined />}
                    aria-label="Export CSV"
                    className="!h-9 !rounded-lg !font-medium"
                  >
                    Export
                  </Button>
                </Tooltip>
              </Dropdown>
            ) : (
              <Tooltip title="Export current view as CSV" placement="top">
                <Button
                  icon={<DownloadOutlined />}
                  onClick={onExport}
                  aria-label="Export CSV"
                  disabled={typeof onExport !== 'function'}
                  className="!h-9 !rounded-lg !font-medium"
                >
                  Export
                </Button>
              </Tooltip>
            )
          ) : null}

          {extra}

          {canAdd ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAdd}
              className="!h-9 !rounded-lg !font-medium !bg-[#1877F2] hover:!bg-blue-700 !border-0 !shadow-sm"
            >
              {addButtonText}
            </Button>
          ) : null}
        </Space>
      </div>
    </div>
  );
}
